import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    IonSearchbar,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonChip,
    IonModal,
    IonProgressBar,
    useIonRouter
} from '@ionic/react';
import {
    barChartOutline,
    addCircleOutline,
    eyeOutline,
    trashOutline,
    timeOutline,
    trendingUpOutline,
    peopleOutline,
    checkmarkCircleOutline,
    closeCircleOutline,
    warningOutline,
    analyticsOutline,
    refreshOutline,
    chevronBackOutline,
    chevronForwardOutline,
    chatbubblesOutline,
    downloadOutline
} from 'ionicons/icons';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getCurrentOrganizationId, getCurrentUserId, buildOrganizationParams } from '../helpers/organizationUtils';
import './View.css';
import Loader from '../components/Loader';

const AdminPolls = () => {
    const router = useIonRouter();
    const modal = useRef(null);

    const [polls, setPolls] = useState([]);
    const [filteredPolls, setFilteredPolls] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedSegment, setSelectedSegment] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPoll, setSelectedPoll] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        expired: 0,
        totalVotes: 0
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);
    const ITEMS_PER_PAGE = 5;

    useEffect(() => {
        fetchPolls(true);

        // Listen for poll creation events
        const handlePollCreated = () => {
            console.log('Poll created event received, refreshing polls...');
            fetchPolls(false);
        };

        // Listen for vote events to refresh poll data
        const handleVoteUpdated = () => {
            console.log('Vote updated event received, refreshing polls...');
            fetchPolls(false);
        };

        window.addEventListener('pollCreated', handlePollCreated);
        window.addEventListener('voteUpdated', handleVoteUpdated);

        return () => {
            window.removeEventListener('pollCreated', handlePollCreated);
            window.removeEventListener('voteUpdated', handleVoteUpdated);
        };
    }, []);

    useEffect(() => {
        filterPolls();
    }, [polls, searchText, selectedSegment]);

    const fetchPolls = async (showFullLoader = false) => {
        let safetyTimeout;
        try {
            if (showFullLoader) {
                setLoading(true);
            }
            safetyTimeout = setTimeout(() => {
                setLoading(false);
            }, 12000);

            setError(null);

            // Check if user is authenticated and has organization access
            const organizationId = getCurrentOrganizationId();
            const userId = getCurrentUserId();

            const userRoleId = sessionStorage.getItem('role_id');
            const isSuperAdmin = userRoleId === '1';

            if (!userId || (!organizationId && !isSuperAdmin)) {
                setError('Authentication required. Please log in as an organization admin.');
                setLoading(false);
                clearTimeout(safetyTimeout);
                return;
            }

            // Build organization-specific parameters
            const params = buildOrganizationParams({
                action: isSuperAdmin ? 'get_general_polls' : 'get_organization_polls'
            });

            console.log('Fetching organization polls with params:', params.toString());

            const response = await axios.get(`${API_BASE_URL}/get_polls_simple.php?${params.toString()}`, { timeout: 8000 });

            console.log('Poll fetch response:', response.data);

            if (response.data.success && Array.isArray(response.data.polls)) {
                // Process the polls to ensure they have the correct structure
                let processedPolls = response.data.polls.map(poll => ({
                    ...poll,
                    answers: Array.isArray(poll.answers) ? poll.answers : [],
                    totalvotes: parseInt(poll.totalvotes) || 0 // Ensure it's a number
                }));

                // Strict filtering for Organization Admins (role 2)
                const userRoleId = sessionStorage.getItem('role_id');
                if (userRoleId === '2') {
                    console.log('Applying strict user_id filtering for Org Admin');
                    processedPolls = processedPolls.filter(poll => String(poll.user_id) === String(userId));
                }

                setPolls(processedPolls);

                // Update stats based on the (filtered) polls
                const total = processedPolls.length;
                const active = processedPolls.filter(poll => !poll.expired).length;
                const expired = processedPolls.filter(poll => poll.expired).length;
                const totalVotes = processedPolls.reduce((sum, poll) => sum + (poll.totalvotes || 0), 0);

                setStats({ total, active, expired, totalVotes });
            } else {
                // Handle case where no polls are found
                setPolls([]);
                setStats({ total: 0, active: 0, expired: 0, totalVotes: 0 });

                if (response.data.error) {
                    setError(response.data.error);
                }
            }
        } catch (error) {
            console.error('Error fetching organization polls:', error);
            setError('Failed to fetch polls. Please check your connection and try again.');

            // Fallback: Set empty state instead of mock data for organization context
            setPolls([]);
            setStats({ total: 0, active: 0, expired: 0, totalVotes: 0 });
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    };

    const filterPolls = useCallback(() => {
        let filtered = polls;

        // Filter by segment
        if (selectedSegment !== 'all') {
            filtered = filtered.filter(poll => {
                switch (selectedSegment) {
                    case 'active': return !poll.expired;
                    case 'expired': return poll.expired;
                    case 'popular': return poll.totalvotes > 5; // Lowered threshold for testing
                    default: return true;
                }
            });
        }

        // Filter by search text
        if (searchText) {
            filtered = filtered.filter(poll =>
                poll.question.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        setFilteredPolls(filtered);
    }, [polls, selectedSegment, searchText]);

    const handlePollAction = async (action, poll) => {
        switch (action) {
            case 'view':
                setSelectedPoll(poll);
                setIsModalOpen(true);
                break;
            case 'edit':
                router.push(`/admin/polls/${poll.id}/edit`);
                break;
            case 'analytics':
                router.push(`/admin/polls/${poll.id}/analytics`);
                break;
            case 'sentiments':
                router.push(`/admin/polls/${poll.id}/sentiments`);
                break;
            case 'delete':
                if (window.confirm('Are you sure you want to delete this poll? This action cannot be undone.')) {
                    try {
                        const userId = getCurrentUserId();
                        const response = await axios.delete(`${API_BASE_URL}/delete_poll.php?poll_id=${poll.id}&user_id=${userId}`);

                        if (response.data.success) {
                            alert('Poll deleted successfully');
                            fetchPolls(); // Refresh the list
                        } else {
                            alert('Failed to delete poll: ' + (response.data.error || 'Unknown error'));
                        }
                    } catch (error) {
                        console.error('Error deleting poll:', error);
                        alert('Failed to delete poll. Please try again.');
                    }
                }
                break;
            case 'feature':
                console.log('Feature poll:', poll.id);
                break;
            case 'refresh':
                fetchPolls();
                break;
            default:
                console.log('Unknown action:', action);
                break;
        }
    };

    const getStatusColor = (poll) => {
        if (poll.expired) return 'danger';
        return 'success';
    };

    const exportPollsReport = async () => {
        setIsExporting(true);

        // --- HACK TO PREVENT HTML2CANVAS STENCIL CRASH ---
        let originalAdoptedSet;
        const descriptor = Object.getOwnPropertyDescriptor(ShadowRoot.prototype, 'adoptedStyleSheets');
        if (descriptor && descriptor.set) {
            originalAdoptedSet = descriptor.set;
            Object.defineProperty(ShadowRoot.prototype, 'adoptedStyleSheets', {
                set: function (sheets) {
                    try {
                        originalAdoptedSet.call(this, sheets);
                    } catch (e) {
                        // Suppress "Sharing constructed stylesheets" crash
                    }
                },
                configurable: true
            });
        }

        setTimeout(async () => {
            try {
                const element = document.getElementById('admin-polls-export-container');
                if (!element) return;

                if (typeof window.html2pdf === 'undefined') {
                    await new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
                        script.onload = resolve;
                        script.onerror = reject;
                        document.head.appendChild(script);
                    });
                }

                const opt = {
                    margin: 0.3,
                    filename: `organization-polls-report.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true, logging: false },
                    jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
                };

                await window.html2pdf().set(opt).from(element).save();
            } catch (err) {
                console.error('Failed to export PDF:', err);
            } finally {
                setIsExporting(false);
                // Restore original setter
                if (originalAdoptedSet) {
                    Object.defineProperty(ShadowRoot.prototype, 'adoptedStyleSheets', {
                        set: originalAdoptedSet,
                        configurable: true
                    });
                }
            }
        }, 800); // give the DOM time to hide pagination buttons and render all polls if needed
    };

    const getPopularityLevel = (votes) => {
        if (votes > 10) return { level: 'High', color: 'success' };
        if (votes > 5) return { level: 'Medium', color: 'warning' };
        if (votes > 0) return { level: 'Low', color: 'medium' };
        return { level: 'None', color: 'light' };
    };

    // Pagination computed values
    const totalPages = Math.ceil(filteredPolls.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    // If exporting, show all filtered polls. Otherwise paginated.
    const paginatedPolls = isExporting ? filteredPolls : filteredPolls.slice(startIndex, endIndex);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="custom-back-button" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <div className="title-container">
                            <span className="title-text">Organization Polls</span>
                        </div>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            className="export-btn-premium mr-2"
                            onClick={exportPollsReport}
                            disabled={loading || isExporting}
                            style={{ '--color': '#6366f1', fontWeight: 'bold' }}
                        >
                            <IonIcon icon={downloadOutline} slot="start" />
                            EXPORT REPORT
                        </IonButton>
                        <IonButton
                            fill="clear"
                            onClick={fetchPolls}
                            disabled={loading}
                            title="Refresh polls"
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader message="Fetching Organization Polls..." />}
                <div id="admin-polls-export-container" className={`polls-container ${isExporting ? 'bg-slate-50 p-4' : ''}`}>
                    {isExporting && (
                        <div className="mb-6 border-b pb-4">
                            <h2 className="text-2xl font-black text-slate-800">Organization Polls Report</h2>
                            <p className="text-sm text-slate-400 mt-2">Generated on: {new Date().toLocaleString()}</p>
                        </div>
                    )}

                    {/* Stats Dashboard */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm">Total Polls</p>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    {isExporting ? <span className="text-xl">📊</span> : <IonIcon icon={barChartOutline} className="text-xl" />}
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm">Active Polls</p>
                                    <p className="text-2xl font-bold">{stats.active}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    {isExporting ? <span className="text-xl">✅</span> : <IonIcon icon={checkmarkCircleOutline} className="text-xl" />}
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-100 text-sm">Expired</p>
                                    <p className="text-2xl font-bold">{stats.expired}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    {isExporting ? <span className="text-xl">❌</span> : <IonIcon icon={closeCircleOutline} className="text-xl" />}
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-indigo-100 text-sm">Total Votes</p>
                                    <p className="text-2xl font-bold">{stats.totalVotes.toLocaleString()}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    {isExporting ? <span className="text-xl">📈</span> : <IonIcon icon={trendingUpOutline} className="text-xl" />}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search - Hidden during export */}
                    {!isExporting && (
                        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                            <div className="flex flex-col-reverse md:flex-col gap-4">
                                <div className="flex md:justify-end items-center space-x-4 w-full">
                                    <IonSegment value={selectedSegment} onIonChange={(e) => setSelectedSegment(e.detail.value)}>
                                        <IonSegmentButton value="all">
                                            <IonLabel>All</IonLabel>
                                        </IonSegmentButton>
                                        <IonSegmentButton value="active">
                                            <IonLabel>Active</IonLabel>
                                        </IonSegmentButton>
                                        <IonSegmentButton value="expired">
                                            <IonLabel>Expired</IonLabel>
                                        </IonSegmentButton>
                                        <IonSegmentButton value="popular">
                                            <IonLabel>Popular</IonLabel>
                                        </IonSegmentButton>
                                    </IonSegment>
                                </div>
                                <div className="w-full">
                                    <IonSearchbar
                                        value={searchText}
                                        onIonInput={(e) => setSearchText(e.detail.value)}
                                        placeholder="Search polls by question..."
                                        className="custom-searchbar"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Polls List */}
                    <div className="space-y-4">
                        {error ? (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                                <IonIcon icon={warningOutline} className="text-red-500 text-4xl mb-3" />
                                <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Polls</h3>
                                <p className="text-red-600 mb-4">{error}</p>
                                <IonButton
                                    color="danger"
                                    fill="outline"
                                    onClick={fetchPolls}
                                >
                                    Try Again
                                </IonButton>
                            </div>
                        ) : filteredPolls.length === 0 ? (
                            <div className="text-center py-12">
                                <IonIcon icon={barChartOutline} className="text-gray-400 text-6xl mb-4" />
                                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Organization Polls Found</h3>
                                <p className="text-gray-500 mb-6">
                                    {searchText || selectedSegment !== 'all'
                                        ? 'No polls match your current filter criteria.'
                                        : 'Your organization hasn\'t created any polls yet.'
                                    }
                                </p>
                                <IonButton
                                    color="primary"
                                    onClick={() => router.push('/page/add')}
                                >
                                    <IonIcon icon={addCircleOutline} slot="start" />
                                    Create First Poll
                                </IonButton>
                            </div>
                        ) : (
                            paginatedPolls.map((poll) => {
                                const popularity = getPopularityLevel(poll.totalvotes);
                                return (
                                    <div key={poll.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                                        <div className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <div
                                                            className="w-4 h-4 rounded-full"
                                                            style={{ backgroundColor: poll.color }}
                                                        ></div>
                                                        <h3 className="text-lg font-bold text-gray-800 flex-1">
                                                            {poll.question}
                                                        </h3>
                                                    </div>

                                                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                                                        <div className="flex items-center space-x-1">
                                                            {isExporting ? <span>👥</span> : <IonIcon icon={peopleOutline} />}
                                                            <span className="font-semibold text-blue-600">{poll.totalvotes || 0} votes</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            {isExporting ? <span>⏱️</span> : <IonIcon icon={timeOutline} />}
                                                            <span>{poll.timeLeft || 'No time limit'}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <span>Created: {new Date(poll.created_at).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center space-x-2">
                                                        {isExporting ? (
                                                            <>
                                                                <span className={`px-2 py-1 text-xs font-bold rounded-lg ${poll.expired ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                                    {poll.expired ? 'Expired' : 'Active'}
                                                                </span>
                                                                <span className="px-2 py-1 text-xs font-bold rounded-lg bg-indigo-100 text-indigo-800">
                                                                    {popularity.level} Engagement
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <IonChip color={getStatusColor(poll)}>
                                                                    {poll.expired ? 'Expired' : 'Active'}
                                                                </IonChip>
                                                                <IonChip color={popularity.color}>
                                                                    {popularity.level} Engagement
                                                                </IonChip>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {!isExporting && (
                                                    <div className="flex flex-col space-y-2 ml-4">
                                                        <IonButton
                                                            size="small"
                                                            fill="clear"
                                                            onClick={() => handlePollAction('view', poll)}
                                                        >
                                                            <IonIcon icon={eyeOutline} />
                                                        </IonButton>
                                                        <IonButton
                                                            size="small"
                                                            fill="clear"
                                                            onClick={() => handlePollAction('analytics', poll)}
                                                            title="Standard Analytics"
                                                        >
                                                            <IonIcon icon={analyticsOutline} />
                                                        </IonButton>
                                                        <IonButton
                                                            size="small"
                                                            fill="clear"
                                                            color="secondary"
                                                            onClick={() => handlePollAction('sentiments', poll)}
                                                            title="User Sentiments & Feedback"
                                                        >
                                                            <IonIcon icon={chatbubblesOutline} />
                                                        </IonButton>
                                                        <IonButton
                                                            size="small"
                                                            fill="clear"
                                                            color="danger"
                                                            onClick={() => handlePollAction('delete', poll)}
                                                        >
                                                            <IonIcon icon={trashOutline} />
                                                        </IonButton>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Qualitative Insight Snippet */}
                                            {poll.reason_count > 0 && (
                                                <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-3 mb-4 flex items-start gap-3">
                                                    <div className="bg-indigo-100 text-indigo-600 p-1.5 rounded-full mt-0.5">
                                                        {isExporting ? <span>💬</span> : <IonIcon icon={chatbubblesOutline} className="text-sm" />}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-xs font-bold text-indigo-800 uppercase tracking-wide mb-1">
                                                            Qualitative Insight • {poll.reason_count} Responses
                                                        </div>
                                                        <p className="text-sm text-slate-600 italic line-clamp-2">
                                                            "{poll.latest_reason}"
                                                        </p>
                                                    </div>
                                                    {!isExporting && (
                                                        <IonButton
                                                            fill="clear"
                                                            size="small"
                                                            className="text-xs m-0"
                                                            onClick={() => handlePollAction('sentiments', poll)}
                                                        >
                                                            View All
                                                        </IonButton>
                                                    )}
                                                </div>
                                            )}

                                            {/* Poll Results Preview */}
                                            {poll.answers && Array.isArray(poll.answers) && poll.answers.length > 0 && (
                                                <div className="space-y-2">
                                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Results Preview:</h4>
                                                    {poll.answers.map((answer, index) => (
                                                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-sm font-medium text-gray-700">
                                                                    {answer.answer}
                                                                </span>
                                                                <span className="text-sm text-gray-500">
                                                                    {answer.votes || 0} votes ({answer.percent || 0}%)
                                                                </span>
                                                            </div>
                                                            {isExporting ? (
                                                                <div style={{ backgroundColor: '#e5e7eb', width: '100%', borderRadius: '9999px', height: '0.5rem', marginTop: '0.25rem' }}>
                                                                    <div style={{ backgroundColor: '#2563eb', height: '100%', borderRadius: '9999px', width: `${answer.percent || 0}%` }}></div>
                                                                </div>
                                                            ) : (
                                                                <IonProgressBar
                                                                    value={(answer.percent || 0) / 100}
                                                                    color="primary"
                                                                    className="h-2"
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-xl shadow-lg p-4 mt-6 gap-3">
                            <div className="text-sm text-gray-600 order-2 sm:order-1">
                                Showing {startIndex + 1} - {Math.min(endIndex, filteredPolls.length)} of {filteredPolls.length} polls
                            </div>
                            <div className="flex items-center space-x-2 order-1 sm:order-2">
                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronBackOutline} slot="icon-only" />
                                </IonButton>

                                {[...Array(totalPages)].map((_, i) => (
                                    <IonButton
                                        key={i + 1}
                                        fill={currentPage === i + 1 ? 'solid' : 'outline'}
                                        size="small"
                                        onClick={() => goToPage(i + 1)}
                                        style={{
                                            '--border-radius': '8px',
                                            minWidth: '36px',
                                            display: (totalPages <= 5 || i < 2 || i >= totalPages - 2 || Math.abs(i + 1 - currentPage) <= 1) ? 'inline-flex' : 'none'
                                        }}
                                    >
                                        {i + 1}
                                    </IonButton>
                                ))}

                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronForwardOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    )}
                </div>

                {/* Poll Detail Modal */}
                <IonModal ref={modal} isOpen={isModalOpen} onDidDismiss={() => setIsModalOpen(false)} className="poll-details-modal">
                    <IonHeader className="ion-no-border">
                        <IonToolbar className="analytics-header">
                            <IonButtons slot="start">
                                <IonButton onClick={() => setIsModalOpen(false)} color="dark">
                                    <IonIcon icon={chevronBackOutline} />
                                </IonButton>
                            </IonButtons>
                            <IonTitle style={{ fontFamily: 'Montserrat', fontWeight: '700', color: '#1e293b' }}>
                                POLLING INTELLIGENCE
                            </IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={() => setIsModalOpen(false)} style={{ fontWeight: '600', color: '#1e293b' }}>
                                    CLOSE
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="view-content" fullscreen>
                        {selectedPoll && (
                            <div className="max-w-4xl mx-auto p-6 space-y-8">
                                {/* Soft Header Card */}
                                <div className="glass-card-beautified p-10 text-left relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4">
                                        <div className={`px-4 py-1.5 rounded-2xl text-xs font-black tracking-widest ${selectedPoll.expired
                                            ? 'bg-rose-100 text-rose-600'
                                            : 'bg-emerald-100 text-emerald-600'
                                            }`}>
                                            {selectedPoll.expired ? 'EXPIRED ARCHIVE' : 'LIVE STATUS'}
                                        </div>
                                    </div>

                                    <div className="insight-icon-container mb-8 shadow-lg scale-110">
                                        <IonIcon icon={analyticsOutline} className="text-2xl text-white" />
                                    </div>

                                    <h2 className="text-3xl md:text-4xl font-black text-slate-800 mb-6 leading-tight max-w-2xl poll-question-responsive text-left">
                                        {selectedPoll.question}
                                    </h2>

                                    <div className="flex flex-wrap justify-start gap-4">
                                        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-2xl border border-slate-100">
                                            <IonIcon icon={peopleOutline} color="primary" />
                                            <span className="text-sm font-bold text-slate-600">{selectedPoll.totalvotes || 0} RESPONSES</span>
                                        </div>
                                        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-2xl border border-slate-100">
                                            <IonIcon icon={trendingUpOutline} color="success" />
                                            <span className="text-sm font-bold text-slate-600 uppercase">{getPopularityLevel(selectedPoll.totalvotes).level} ENGAGEMENT</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Refined Context Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="analytics-card-glass stat-card-premium border-none">
                                        <p className="text-[10px] font-black text-slate-400 tracking-[0.2em] mb-2 uppercase">Core Participation</p>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-5xl font-black text-slate-800 tracking-tighter">
                                                {selectedPoll.totalvotes || 0}
                                            </span>
                                            <span className="text-slate-400 font-bold">Total Nodes</span>
                                        </div>
                                    </div>
                                    <div className="analytics-card-glass stat-card-premium border-none">
                                        <p className="text-[10px] font-black text-slate-400 tracking-[0.2em] mb-2 uppercase">Temporal Window</p>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-2xl font-black text-slate-800 uppercase">
                                                {selectedPoll.timeLeft || 'INDEFINITE'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-400 mt-2 font-semibold">REMAINING DURATION</p>
                                    </div>
                                </div>

                                {/* Results Section */}
                                {selectedPoll.answers && Array.isArray(selectedPoll.answers) && (
                                    <div className="analytics-card-glass p-8 border-none">
                                        <div className="flex items-center gap-3 mb-8">
                                            <div className="insight-icon-container">
                                                <IonIcon icon={barChartOutline} className="text-xl text-white" />
                                            </div>
                                            <h4 className="text-2xl font-black text-slate-800">Intelligence Distribution</h4>
                                        </div>
                                        <div className="space-y-6">
                                            {selectedPoll.answers.map((answer, index) => {
                                                const colors = [
                                                    { gradient: 'bg-emerald-500', bg: 'bg-emerald-50/50', text: 'text-emerald-700' },
                                                    { gradient: 'bg-blue-500', bg: 'bg-blue-50/50', text: 'text-blue-700' },
                                                    { gradient: 'bg-indigo-500', bg: 'bg-indigo-50/50', text: 'text-indigo-700' },
                                                    { gradient: 'bg-violet-500', bg: 'bg-violet-50/50', text: 'text-violet-700' }
                                                ];
                                                const colorScheme = colors[index % colors.length];

                                                return (
                                                    <div key={index} className={`${colorScheme.bg} rounded-3xl p-6 border border-white`}>
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex-1">
                                                                <h5 className={`text-lg font-black ${colorScheme.text} mb-1 tracking-tight`}>
                                                                    {answer.answer}
                                                                </h5>
                                                                <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">
                                                                    {answer.votes || 0} COLLECTED RESPONSES
                                                                </p>
                                                            </div>
                                                            <div className="text-3xl font-black text-slate-800 tracking-tighter">
                                                                {answer.percent || 0}%
                                                            </div>
                                                        </div>

                                                        {/* Modern Slim Progress Bar */}
                                                        <div className="w-full h-2 bg-white/50 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full ${colorScheme.gradient} rounded-full transition-all duration-1000 ease-out`}
                                                                style={{ width: `${answer.percent || 0}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Premium Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                                    <IonButton
                                        onClick={() => {
                                            router.push(`/admin/polls/${selectedPoll.id}/analytics`);
                                            setIsModalOpen(false);
                                        }}
                                        className="action-btn-beautified"
                                        style={{ '--background': '#22c55e', '--color': 'white' }}
                                    >
                                        <IonIcon icon={analyticsOutline} slot="start" />
                                        VIEW DETAILED ARCHIVE
                                    </IonButton>
                                    <IonButton
                                        fill="clear"
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to delete this poll?')) {
                                                handlePollAction('delete', selectedPoll);
                                            }
                                        }}
                                        className="action-btn-beautified"
                                        style={{ '--color': '#ef4444' }}
                                    >
                                        <IonIcon icon={trashOutline} slot="start" />
                                        ARCHIVE & REMOVE
                                    </IonButton>
                                </div>
                            </div>
                        )}
                    </IonContent>
                </IonModal>
            </IonContent>
        </IonPage >
    );
};

export default AdminPolls;